"use client";

import React from "react";
import { Modal, Button } from "antd";
import {
  CheckOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import ReactDOM from "react-dom/client";

// Icon map by type
const iconMap = {
  success: <CheckOutlined />,
  warning: <ExclamationCircleOutlined />,
  error: <CloseCircleOutlined />,
  info: <InfoCircleOutlined />,
  copied: <CopyOutlined />,
};

// Default config for each type
const defaultValues = {
  success: {
    title: "Success",
    message: "Operation was successful.",
    iconBgColor: "#e6f7e9",
    iconColor: "#52c41a",
    primaryButtonText: "OK",
    secondaryButtonText: null,
  },
  warning: {
    title: "Warning",
    message: "This action might have consequences.",
    iconBgColor: "#fff7e6",
    iconColor: "#fa8c16",
    primaryButtonText: "Proceed",
    secondaryButtonText: "Cancel",
  },
  error: {
    title: "Error",
    message: "Something went wrong.",
    iconBgColor: "#fff1f0",
    iconColor: "#ff4d4f",
    primaryButtonText: "OK",
    secondaryButtonText: null,
  },
  info: {
    title: "Information",
    message: "Here's some helpful info.",
    iconBgColor: "#e6f4ff",
    iconColor: "#1890ff",
    primaryButtonText: "Got it",
    secondaryButtonText: null,
  },
  copied: {
    title: "Copied",
    message: "Text copied to clipboard.",
    iconBgColor: "#f0f5ff",
    iconColor: "#722ed1",
    primaryButtonText: "OK",
    secondaryButtonText: null,
  },
};

// FeedbackModal component
const FeedbackModal = ({
  visible,
  type = "success",
  title,
  message,
  primaryButtonText,
  secondaryButtonText,
  onPrimaryAction,
  onSecondaryAction,
  afterClose,
  onClose,
}) => {
  const current = defaultValues[type] || defaultValues.info;

  return (
    <Modal
      open={visible}
      footer={null}
      closable={false}
      onCancel={onClose}
      afterClose={afterClose}
      width={400}
      centered
      style={{ borderRadius: "12px", overflow: "hidden" }}
      bodyStyle={{ padding: "10px" }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "start",
          textAlign: "start",
          overflow: "hidden",
        }}
      >
        {/* Icon */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "64px",
            height: "64px",
            borderRadius: "50%",
            backgroundColor: current.iconBgColor,
            marginBottom: "16px",
          }}
        >
          {React.cloneElement(iconMap[type] || iconMap.info, {
            style: { fontSize: "24px", color: current.iconColor },
          })}
        </div>

        {/* Title */}
        <h3 style={{ fontSize: "20px", fontWeight: "600", margin: 0 }}>
          {title || current.title}
        </h3>

        {/* Message */}
        <p
          style={{
            fontSize: "14px",
            color: "#666",
            marginBottom: "24px",
            marginTop: 0,
          }}
        >
          {message || current.message}
        </p>

        {/* Buttons */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "12px",
            width: "100%",
            marginTop: 10,
          }}
        >
          {onSecondaryAction && current.secondaryButtonText && (
            <Button
              style={{
                width: "47%",
                height: "40px",
                borderRadius: "8px",
                color: "#344054",
                border: "1px solid #D0D5DD",
                fontWeight: 600,
              }}
              onClick={onSecondaryAction}
            >
              {secondaryButtonText || current.secondaryButtonText}
            </Button>
          )}
          {onPrimaryAction && (
            <Button
              type="primary"
              style={{
                width: onSecondaryAction ? "47%" : "100%",
                height: "40px",
                backgroundColor: "#BA24D5",
                border: "1px solid #BA24D5",
                borderRadius: "8px",
                fontWeight: 600,
              }}
              onClick={onPrimaryAction}
            >
              {primaryButtonText || current.primaryButtonText}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

// Singleton modal root
let modalContainer = null;
let root = null;

export const AlertMsg = (type, message) => {
  try {
    // console.log("AlertMsg function called with:", { type, message });

    // If a modal is already open, unmount it before showing a new one
    if (modalContainer) {
      root?.unmount?.();
      modalContainer.remove?.();
      modalContainer = null;
      document.body.style.overflow = "";
    }

    modalContainer = document.createElement("div");
    document.body.appendChild(modalContainer);
    document.body.style.overflow = "hidden";

    root = ReactDOM.createRoot(modalContainer);

    const closeModal = () => {
      root?.unmount?.();
      modalContainer?.remove?.();
      modalContainer = null;
      root = null;
      document.body.style.overflow = "";
    };

    // Ensure message is a string and not empty
    const displayMessage =
      typeof message === "string" && message.trim()
        ? message.trim()
        : "An error occurred";

    // console.log("Final message to display:", displayMessage);

    root.render(
      <FeedbackModal
        visible={true}
        type={type.toLowerCase()}
        message={displayMessage}
        onPrimaryAction={closeModal}
        onClose={closeModal}
        afterClose={closeModal}
      />
    );
  } catch (error) {
    // console.error("Modal error:", error);
    alert(message || "An error occurred");
  }
};

export default FeedbackModal;
